import React from 'react';

import { gql, useQuery } from '@apollo/client';
import {
  Typography,
  Stack,
  Divider,
  Box,
  Link as MuiLink,
  Button,
} from '@mui/material';
import { Link, useParams } from 'react-router-dom';
import { ItsAlmostTimeToParty } from '../ItsAlmostTimeToParty';
import { NavigationBar } from '../NavigationBar';
import { PageContainer } from '../styledComponents';
import { FooterBar } from '../../../components/FooterBar';
import { VENUE_BOOKINGS_QUERY } from '../graphql';
import {
  VenueBookingsQuery,
  VenueBookingsQueryVariables,
} from '@pv/common/graphql/operations';
import { EventPlan } from '../../../components/EventPlan';
import { EVENT_PLAN_FIELDS } from '../../../components/EventPlan/graphql';

const GET_MARKETPLACE_EVENT_WITH_PLAN = gql`
  query GetMarketplaceEventWithPlan($eventSlug: String!) {
    marketplaceEvent(slug: $eventSlug) {
      id
      eventPlan {
        ...EventPlanFields
      }
    }
  }
  ${EVENT_PLAN_FIELDS}
`;

export const MarketplaceEventSuccessPage = () => {
  const { eventSlug, venueSlug } = useParams();

  const { data: venueData } = useQuery<
    VenueBookingsQuery,
    VenueBookingsQueryVariables
  >(VENUE_BOOKINGS_QUERY, {
    skip: !venueSlug,
    variables: { venueSlug: venueSlug! },
  });

  const { data: eventData } = useQuery(GET_MARKETPLACE_EVENT_WITH_PLAN, {
    skip: !eventSlug,
    variables: { eventSlug: eventSlug! },
  });

  const venue = venueData?.marketplaceVenue;
  const eventPlan = eventData?.marketplaceEvent?.eventPlan;

  return (
    <>
      <NavigationBar />
      <PageContainer>
        <Stack
          direction="column"
          justifyContent="center"
          alignItems="center"
          spacing={2}
        >
          <Typography
            variant="h1"
            sx={{
              fontSize: '30px',
              lineHeight: '34.5px',
              fontWeight: '700',
            }}
          >
            Request Sent
          </Typography>
          <ItsAlmostTimeToParty />
          <Stack direction="column" spacing={2} maxWidth="720px">
            <Stack direction="column" spacing={2}>
              <Typography
                variant="body1"
                sx={{
                  fontSize: '20px',
                  lineHeight: '30px',
                  fontWeight: '400',
                }}
              >
                Your inquiry has been sent to {venue?.name || 'the venue'}, and
                you can expect to hear from them soon.{' '}
                {venue?.averageResponseTime && (
                  <em>
                    They typically respond within {venue.averageResponseTime}.
                  </em>
                )}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: '20px',
                  lineHeight: '30px',
                  fontWeight: '400',
                }}
              >
                In the meantime, we've created an{' '}
                <MuiLink href={`/guests/event-plans`} target="_blank">
                  event
                </MuiLink>{' '}
                for you and added this inquiry. You can add more inquiries to
                the same event to keep everything organized.
              </Typography>
            </Stack>
            {eventPlan && (
              <Box>
                <EventPlan eventPlan={eventPlan} cta={false} />
              </Box>
            )}
            <Button
              variant="contained"
              color="secondary"
              size="large"
              component={Link}
              to="/"
            >
              Find More Venues
            </Button>
          </Stack>
        </Stack>

        <div style={{ flexGrow: 1 }} />
        <div>
          <Divider />
          <FooterBar />
        </div>
      </PageContainer>
    </>
  );
};
